import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowRight, Clock, Tag, Flame, Eye, Search, X } from 'lucide-react';
import { articlesApi } from '../lib/api';
import { LeadCaptureForm } from '../components/forms/LeadCaptureForm';
import { PageSEO } from '../components/seo/SchemaMarkup';
import { ShareButton } from '../components/ShareButton';
import { getVideoForArticle } from '../lib/categoryVideos';
import { BookmarkButton } from '../components/BookmarkButton';

const categories = ['All', 'HYROX', 'Peptides', 'Biohacking', 'Fitness', 'Nutrition', 'Wellness', 'Motivation', 'Martial Arts', 'Culture', 'Meditation', 'Technology'];

// Global video loading manager - limits concurrent video loads
const videoLoadManager = (() => {
  let activeLoads = 0;
  const MAX_CONCURRENT = 4;
  const queue = [];
  
  const processQueue = () => {
    while (queue.length > 0 && activeLoads < MAX_CONCURRENT) {
      const next = queue.shift();
      if (next.cancelled) continue;
      activeLoads++;
      next.execute(() => {
        activeLoads--;
        processQueue();
      });
    }
  };
  
  return {
    enqueue: (executeFn) => {
      const entry = { execute: executeFn, cancelled: false };
      queue.push(entry);
      processQueue();
      return () => { entry.cancelled = true; };
    }
  };
})();

// Lazy Video Background - preloads when visible, plays on hover
const LazyVideo = ({ videoUrl, poster, className, isHovered }) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const cancelRef = useRef(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || !videoUrl) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setShouldLoad(true);
      },
      { rootMargin: '200px', threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [videoUrl]);

  useEffect(() => {
    if (!shouldLoad || !videoUrl) return;
    cancelRef.current = videoLoadManager.enqueue((done) => {
      const vid = videoRef.current;
      if (!vid) { done(); return; }
      vid.src = videoUrl;
      vid.oncanplay = () => { setVideoReady(true); done(); };
      vid.onerror = () => done();
      vid.load();
    });
    return () => { if (cancelRef.current) cancelRef.current(); };
  }, [shouldLoad, videoUrl]);

  useEffect(() => {
    const vid = videoRef.current;
    if (!vid || !videoReady) return;
    if (isHovered) {
      vid.currentTime = 0;
      vid.play().catch(() => {});
    } else {
      vid.pause();
    }
  }, [isHovered, videoReady]);

  return (
    <div ref={containerRef} className="w-full h-full">
      <img
        src={poster}
        alt=""
        className={`${className} absolute inset-0`}
        onError={(e) => {
          e.target.style.display = 'none';
          e.target.parentElement.style.background = 'linear-gradient(135deg, #1a1a1a 0%, #2d1f1f 50%, #1a1a1a 100%)';
        }}
      />
      {shouldLoad && videoUrl && (
        <video
          ref={videoRef}
          muted
          loop
          playsInline
          preload="none"
          className={`${className} absolute inset-0 ${isHovered && videoReady ? 'opacity-100' : 'opacity-0'} transition-opacity duration-500`}
        />
      )}
    </div>
  );
};

// Article Card Component
const ArticleCard = ({ article, size = 'normal', isPopular = false, isLatest = false, index = 0 }) => {
  const isLarge = size === 'large';
  const isFeatured = size === 'featured';
  const videoUrl = getVideoForArticle(article.category, index);
  const [hovered, setHovered] = useState(false);
  
  return (
    <Link 
      to={`/blogs/article/${article.slug}`}
      className={`group block relative overflow-hidden ${
        isFeatured ? 'md:col-span-8 md:row-span-2' : 
        isLarge ? 'md:col-span-4 md:row-span-2' : 
        'md:col-span-4'
      }`}
      data-testid={`article-card-${article.slug}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className={`relative ${isFeatured ? 'h-[500px]' : isLarge ? 'h-[400px]' : 'h-[280px]'} overflow-hidden`}>
        <LazyVideo
          videoUrl={videoUrl}
          poster={article.featured_image}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          isHovered={hovered}
        />
        
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />

        {/* Latest / Popular Badges */}
        {isLatest ? (
          <div 
            className="absolute top-4 right-4 flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-[#39FF14] to-[#00F0FF] text-black text-[10px] font-bold uppercase tracking-wider z-10 shadow-lg shadow-[#39FF14]/30"
            data-testid={`latest-badge-${article.slug}`}
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-black opacity-60" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-black" />
            </span>
            New &middot; Just Published
          </div>
        ) : isPopular && (
          <div 
            className="absolute top-4 right-4 flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-orange-600 to-red-600 text-white text-[10px] font-bold uppercase tracking-wider z-10"
            data-testid={`popular-badge-${article.slug}`}
          >
            <Flame className="w-3 h-3" />
            Popular
          </div>
        )}

        {/* Bookmark */}
        <div className="absolute top-4 left-4 z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <BookmarkButton item={{
            type: 'article',
            item_id: article.slug,
            title: article.title,
            image: article.featured_image,
            category: article.category,
            excerpt: article.excerpt || '',
          }} />
        </div>
        
        {/* Content */}
        <div className="absolute bottom-0 left-0 right-0 p-6">
          {/* Category Tag */}
          <span className="inline-block px-3 py-1 text-xs uppercase tracking-wider bg-[#E63946] text-white mb-3">
            {article.category}
          </span>
          
          {/* Title */}
          <h3 
            className={`text-white mb-2 group-hover:text-[#00F0FF] transition-colors duration-300 ${
              isFeatured ? 'text-3xl md:text-4xl' : isLarge ? 'text-2xl' : 'text-xl'
            }`}
            style={{ fontFamily: 'Bebas Neue, sans-serif' }}
          >
            {article.title}
          </h3>
          
          {/* Excerpt - only show on larger cards */}
          {(isFeatured || isLarge) && (
            <p className="text-zinc-400 text-sm mb-4 line-clamp-2">
              {article.excerpt}
            </p>
          )}
          
          {/* Meta */}
          <div className="flex items-center gap-4 text-zinc-500 text-xs">
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {article.read_time} min read
            </span>
            {article.view_count > 0 && (
              <span className="flex items-center gap-1">
                <Eye className="w-3 h-3" />
                {article.view_count >= 1000 ? `${(article.view_count / 1000).toFixed(1)}k` : article.view_count} views
              </span>
            )}
          </div>
        </div>
        
        {/* Hover border effect */}
        <div className="absolute inset-0 border-2 border-transparent group-hover:border-[#E63946]/50 transition-colors duration-300" />
      </div>
    </Link>
  );
};

// Blog List Item (for smaller listings)
const ArticleListItem = ({ article }) => (
  <Link 
    to={`/blogs/article/${article.slug}`}
    className="group flex gap-4 py-6 border-b border-zinc-800 hover:border-[#E63946]/50 transition-colors duration-300"
    data-testid={`article-list-${article.slug}`}
  >
    <div className="w-24 h-24 flex-shrink-0 overflow-hidden">
      <img 
        src={article.featured_image} 
        alt={article.title}
        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
      />
    </div>
    <div className="flex-1 min-w-0">
      <span className="text-xs text-[#E63946] uppercase tracking-wider">{article.category}</span>
      <h4 
        className="text-white text-lg mt-1 group-hover:text-[#00F0FF] transition-colors duration-300 line-clamp-2"
        style={{ fontFamily: 'Bebas Neue, sans-serif' }}
      >
        {article.title}
      </h4>
      <p className="text-zinc-500 text-sm mt-1 line-clamp-1">{article.excerpt}</p>
    </div>
  </Link>
);

export default function BlogPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialCategory = searchParams.get('category') || 'All';
  const [articles, setArticles] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(initialCategory);
  const [loading, setLoading] = useState(true);
  const [popularSlugs, setPopularSlugs] = useState(new Set());
  // Admin-controlled featured slugs (set in Admin → Featured Article Tab via featured_order)
  const [featuredSlugs, setFeaturedSlugs] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const searchTimeoutRef = useRef(null);

  // Sync category from URL params
  useEffect(() => {
    const cat = searchParams.get('category');
    if (cat && categories.includes(cat)) {
      setSelectedCategory(cat);
    }
  }, [searchParams]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [articlesRes, popularRes, featuredRes] = await Promise.all([
          articlesApi.getAll({ published: true, limit: 600, sort: '-created_at' }),
          articlesApi.getPopular(5).catch(() => ({ data: [] })),
          articlesApi.getFeaturedList(10, true).catch(() => ({ data: [] })),
        ]);
        if (articlesRes.data && articlesRes.data.length > 0) {
          setArticles(articlesRes.data);
        }
        if (popularRes.data && popularRes.data.length > 0) {
          setPopularSlugs(new Set(popularRes.data.map(a => a.slug)));
        }
        if (featuredRes.data && featuredRes.data.length > 0) {
          // API returns sorted by featured_order ASC; preserve admin order
          setFeaturedSlugs(featuredRes.data.map(a => a.slug));
        }
      } catch (error) {
        console.error('Failed to load articles', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Debounced search
  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (!searchQuery.trim() || searchQuery.trim().length < 2) {
      setSearchResults(null);
      setSearching(false);
      return;
    }
    setSearching(true);
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const res = await articlesApi.search(searchQuery.trim());
        setSearchResults(res.data);
      } catch (e) {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 350);
    return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); };
  }, [searchQuery]);

  // Sort: admin-pinned featured articles first (in featured_order), then rest by created_at desc
  const filteredArticles = (() => {
    const inCategory = selectedCategory === 'All'
      ? articles
      : articles.filter(a => a.category === selectedCategory);

    const pinned = [];
    const pinnedSet = new Set();
    for (const slug of featuredSlugs) {
      const found = inCategory.find(a => a.slug === slug);
      if (found) {
        pinned.push(found);
        pinnedSet.add(slug);
      }
    }

    const rest = inCategory
      .filter(a => !pinnedSet.has(a.slug))
      .sort((a, b) => {
        const da = new Date(a.created_at || 0).getTime();
        const db = new Date(b.created_at || 0).getTime();
        return db - da;
      });

    return [...pinned, ...rest];
  })();

  // Auto-detect the most recently published article by created_at — gets a "NEW" badge
  const latestSlug = (() => {
    if (!articles || articles.length === 0) return null;
    const sorted = [...articles].sort((a, b) => {
      const da = new Date(a.created_at || 0).getTime();
      const db = new Date(b.created_at || 0).getTime();
      return db - da;
    });
    return sorted[0]?.slug || null;
  })();

  const featuredArticle = filteredArticles[0];
  const secondaryArticles = filteredArticles.slice(1, 3);
  const remainingArticles = filteredArticles.slice(3);

  // Articles published within the last 7 days — surfaced in the "New this week" strip
  const newThisWeek = (() => {
    const cutoff = Date.now() - 7 * 24 * 3600 * 1000;
    return articles
      .filter((a) => new Date(a.created_at || 0).getTime() >= cutoff)
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      .slice(0, 8);
  })();

  return (
    <main className="pt-20 min-h-screen" data-testid="blog-page">
      <PageSEO
        title="Biohacking, Peptide & Fitness Blog | NinjAthlete"
        description="160+ science-backed articles on peptide therapy, biohacking, HYROX training, nutrition, and performance optimization. Updated weekly."
        path="/blog"
        breadcrumbs={[{ name: 'Home', url: '/' }, { name: 'Blog', url: '/blog' }]}
      />
      {/* Hero Section */}
      <section className="container-ninja section-spacing">
        <div className="max-w-3xl">
          <p className="text-ninja-label text-[#00F0FF] mb-4">Knowledge is Power</p>
          <h1 className="text-white mb-6" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
            The Ninja's <span className="text-gradient-ninja">Journal</span>
          </h1>
          <p className="text-zinc-400 text-lg leading-relaxed mb-8">
            Deep dives into biohacking, meditation, nutrition, and performance optimization. 
            Science-backed insights for those pursuing mastery.
          </p>
          <div className="flex items-center gap-3 mb-8">
            <ShareButton title="The Ninja's Journal — Biohacking & Fitness Blog | NinjAthlete" path="/blog" accentColor="#00F0FF" />
          </div>
          
          {/* Search Bar */}
          <div className="relative max-w-lg" data-testid="article-search-container">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search articles..."
              className="w-full pl-12 pr-10 py-3 bg-zinc-900 border border-zinc-700 text-white placeholder-zinc-500 focus:border-[#E63946] focus:ring-1 focus:ring-[#E63946] focus:outline-none transition-colors duration-300"
              data-testid="article-search-input"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white transition-colors"
                data-testid="article-search-clear"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Search Results Dropdown */}
          {searchQuery.trim().length >= 2 && (
            <div className="relative max-w-lg mt-1" data-testid="search-results-dropdown">
              <div className="absolute top-0 left-0 right-0 bg-zinc-900 border border-zinc-700 z-50 max-h-80 overflow-y-auto shadow-2xl">
                {searching ? (
                  <div className="p-4 text-center text-zinc-500 text-sm">Searching...</div>
                ) : searchResults && searchResults.length > 0 ? (
                  searchResults.map((article) => (
                    <Link
                      key={article.slug}
                      to={`/blogs/article/${article.slug}`}
                      className="flex items-start gap-3 p-3 hover:bg-zinc-800 transition-colors border-b border-zinc-800 last:border-b-0"
                      data-testid={`search-result-${article.slug}`}
                    >
                      {article.featured_image && (
                        <img src={article.featured_image} alt="" className="w-12 h-12 object-cover flex-shrink-0" />
                      )}
                      <div className="min-w-0">
                        <span className="text-[10px] uppercase tracking-wider text-[#E63946]">{article.category}</span>
                        <h4 className="text-white text-sm font-medium line-clamp-1">{article.title}</h4>
                        {article.read_time && (
                          <span className="text-zinc-500 text-xs">{article.read_time} min read</span>
                        )}
                      </div>
                    </Link>
                  ))
                ) : searchResults && searchResults.length === 0 ? (
                  <div className="p-4 text-center text-zinc-500 text-sm">No articles found for "{searchQuery}"</div>
                ) : null}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* New This Week — fresh articles strip */}
      {newThisWeek.length > 0 && (
        <section className="container-ninja pb-10" data-testid="new-this-week-strip">
          <div className="flex items-center gap-3 mb-4">
            <span className="px-2.5 py-1 bg-[#C7FA3E] text-black text-[10px] font-bold uppercase tracking-[0.2em]" data-testid="new-this-week-badge">
              New this week
            </span>
            <span className="text-zinc-500 text-xs uppercase tracking-wider">
              {newThisWeek.length} fresh article{newThisWeek.length === 1 ? '' : 's'}
            </span>
          </div>
          <div className="flex gap-4 overflow-x-auto pb-2">
            {newThisWeek.map((a) => (
              <Link
                key={a.slug}
                to={`/blogs/article/${a.slug}`}
                className="flex-none w-64 bg-zinc-900 border border-zinc-800 hover:border-[#C7FA3E]/50 transition-colors duration-300 group"
                data-testid={`new-week-card-${a.slug}`}
              >
                {a.featured_image && (
                  <img src={a.featured_image} alt="" className="w-full h-28 object-cover" loading="lazy" />
                )}
                <div className="p-3">
                  <span className="text-[10px] uppercase tracking-wider text-[#E63946]">{a.category}</span>
                  <h4 className="text-white text-sm font-medium line-clamp-2 group-hover:text-[#C7FA3E] transition-colors duration-300">
                    {a.title}
                  </h4>
                  <span className="text-zinc-500 text-[11px]">
                    {new Date(a.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    {a.read_time ? ` · ${a.read_time} min` : ''}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Category Filter */}
      <section className="container-ninja pb-12">
        <div className="flex flex-wrap gap-3">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => {
                setSelectedCategory(category);
                if (category === 'All') setSearchParams({});
                else setSearchParams({ category });
              }}
              className={`px-4 py-2 text-sm uppercase tracking-wider transition-all duration-300 ${
                selectedCategory === category
                  ? 'bg-[#E63946] text-white'
                  : 'bg-zinc-900 text-zinc-400 hover:text-white hover:bg-zinc-800'
              }`}
              style={{ fontFamily: 'Bebas Neue, sans-serif' }}
              data-testid={`filter-${category.toLowerCase()}`}
            >
              {category}
            </button>
          ))}
        </div>
      </section>

      {/* Bento Grid */}
      {filteredArticles.length > 0 && (
        <section className="container-ninja pb-24">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
            {/* Featured Article */}
            {featuredArticle && (
              <ArticleCard 
                article={featuredArticle} 
                size="featured" 
                isPopular={popularSlugs.has(featuredArticle.slug)} 
                isLatest={featuredArticle.slug === latestSlug}
                index={0} 
              />
            )}
            
            {/* Secondary Articles */}
            {secondaryArticles.map((article, i) => (
              <ArticleCard 
                key={article.id || article.slug} 
                article={article} 
                size="large" 
                isPopular={popularSlugs.has(article.slug)} 
                isLatest={article.slug === latestSlug}
                index={i + 1} 
              />
            ))}
            
            {/* Remaining Articles */}
            {remainingArticles.map((article, i) => (
              <ArticleCard 
                key={article.id || article.slug} 
                article={article} 
                isPopular={popularSlugs.has(article.slug)} 
                isLatest={article.slug === latestSlug}
                index={i + 3} 
              />
            ))}
          </div>
        </section>
      )}

      {/* Newsletter CTA */}
      <section className="bg-zinc-900/50 py-24">
        <div className="container-ninja">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-ninja-label text-[#E63946] mb-4">Stay Sharp</p>
              <h2 className="text-white mb-4" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                Weekly Insights Delivered
              </h2>
              <p className="text-zinc-400">
                Get the latest biohacking research, meditation techniques, and performance tips. 
                Join the dojo and level up your game.
              </p>
            </div>
            <LeadCaptureForm 
              source="blog_page"
              variant="inline"
              buttonText="Subscribe"
              interests={['blog', 'newsletter']}
            />
          </div>
        </div>
      </section>
    </main>
  );
}
